import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  RefreshCw,
  Home,
  ShieldAlert,
  WifiOff,
  FileQuestion,
  ServerCrash,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import {
  classifyError,
  logError,
  type ClassifiedError,
} from "@/lib/errors/error-types";
import { useEffect } from "react";
import { getActiveModulesByContext } from "@/lib/modules";

interface ErrorComponentProps {
  error: unknown;
  reset?: () => void;
  context?: {
    route?: string;
    userId?: string;
    action?: string;
  };
}

function getErrorIcon(type: ClassifiedError["type"]) {
  switch (type) {
    case "auth":
      return ShieldAlert;
    case "network":
      return WifiOff;
    case "not-found":
      return FileQuestion;
    case "server":
      return ServerCrash;
    case "permission":
      return ShieldAlert;
    case "validation":
    case "unknown":
    default:
      return AlertTriangle;
  }
}

function getErrorColor(type: ClassifiedError["type"]) {
  switch (type) {
    case "auth":
    case "permission":
      return "text-orange-500 bg-orange-500/10";
    case "network":
      return "text-blue-500 bg-blue-500/10";
    case "not-found":
      return "text-muted-foreground bg-muted";
    case "server":
    case "unknown":
      return "text-destructive bg-destructive/10";
    case "validation":
    default:
      return "text-yellow-500 bg-yellow-500/10";
  }
}

export function ErrorComponent({ error, reset, context }: ErrorComponentProps) {
  const navigate = useNavigate();
  const classifiedError = classifyError(error);
  const Icon = getErrorIcon(classifiedError.type);
  const colorClass = getErrorColor(classifiedError.type);

  useEffect(() => {
    logError(classifiedError, context);
  }, [classifiedError, context]);

  const handleSignIn = () => {
    navigate({ to: "/auth/sign-in" });
  };

  const handleGoHome = () => {
    navigate({ to: "/dashboard" });
  };

  const handleRetry = () => {
    if (reset) {
      reset();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <Card className="w-full max-w-md border-border">
        <CardHeader className="text-center">
          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${colorClass}`}
          >
            <Icon className="h-8 w-8" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {classifiedError.type === "auth" && "Sesión Expirada"}
            {classifiedError.type === "permission" && "Acceso Denegado"}
            {classifiedError.type === "network" && "Sin Conexión"}
            {classifiedError.type === "not-found" && "No Encontrado"}
            {classifiedError.type === "server" && "Error del Servidor"}
            {classifiedError.type === "validation" && "Error de Validación"}
            {classifiedError.type === "unknown" && "Algo salió mal"}
          </CardTitle>
          <CardDescription className="mt-2">
            {classifiedError.message}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            {classifiedError.requiresAuth && (
              <Button
                variant="default"
                className="flex-1"
                onClick={handleSignIn}
              >
                <ShieldAlert className="mr-2 h-4 w-4" />
                Iniciar Sesión
              </Button>
            )}

            {classifiedError.canRetry && !classifiedError.requiresAuth && (
              <Button
                variant="default"
                className="flex-1"
                onClick={handleRetry}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reintentar
              </Button>
            )}

            {!classifiedError.requiresAuth && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/" + getActiveModulesByContext("main")[0].routes[0],
                  })
                }
                className="w-full"
              >
                ir a {getActiveModulesByContext("main")[0].name}
              </Button>
            )}
          </div>

          {import.meta.env.DEV && (
            <details className="mt-4 rounded-md border border-border bg-muted/50 p-3">
              <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                Detalles técnicos
              </summary>
              <pre className="mt-2 overflow-auto text-xs text-muted-foreground">
                {JSON.stringify(
                  {
                    type: classifiedError.type,
                    message: classifiedError.message,
                    error:
                      classifiedError.originalError instanceof Error
                        ? {
                            name: classifiedError.originalError.name,
                            message: classifiedError.originalError.message,
                            stack: classifiedError.originalError.stack,
                          }
                        : classifiedError.originalError,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
